// SCR-STAFF-950 — کانال‌ها و خطوط (US-MSG-001 تا 006، 022؛ AP-MSG-01، AP-MSG-02): خط خدماتی مشترک یا اختصاصی (فقط با اتصال فعال پیامک با
// اعتبارنامه خود مرکز در INT، وگرنه PHX-MSG-0401؛ خط اختصاصی بدون امضای نام مرکز)، خط تبلیغاتی، جای امضای نام کوتاه مرکز در خط مشترک
// (BR-MSG-002)، ساعات مجاز Transactional و Marketing (BR-MSG-023)، سقف Marketing، وضعیت Go-live و مانده اعتبار پیامک، وضعیت کانال ایمیل و
// Web Push (زنجیره جایگزین ۱۰ دقیقه — US-MSG-006)؛ ارسال پیامک آزمایشی به موبایل کاربر جاری.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ChannelAvailabilityView,
  type MessagingSettingsView,
  type UpdateMessagingSettingsRequest,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  CheckboxRHF,
  ErrorState,
  faDigits,
  FormPage,
  KeyValueList,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { msgKeys, SCR_STAFF_950 } from './common';

const HOUR = /^([01]\d|2[0-3]):[0-5]\d$/;

const schema = z.object({
  serviceLine: z.enum(['Shared', 'Dedicated']),
  advertisingEnabled: z.boolean(),
  signature: z.enum(['Prefix', 'Suffix']),
  transactionalFrom: z.string().regex(HOUR, 'msg:channels.hour_invalid'),
  transactionalTo: z.string().regex(HOUR, 'msg:channels.hour_invalid'),
  marketingFrom: z.string().regex(HOUR, 'msg:channels.hour_invalid'),
  marketingTo: z.string().regex(HOUR, 'msg:channels.hour_invalid'),
  marketingCap: z.coerce.number().int().min(0).max(30),
});

type FormValues = z.input<typeof schema>;

function toForm(view: MessagingSettingsView): FormValues {
  return {
    serviceLine: view.serviceLine,
    advertisingEnabled: view.advertisingEnabled,
    signature: view.signature,
    transactionalFrom: view.transactionalFrom,
    transactionalTo: view.transactionalTo,
    marketingFrom: view.marketingFrom,
    marketingTo: view.marketingTo,
    marketingCap: Number(view.marketingCap),
  };
}

function ChannelStatus({ view, labelKey }: { view: ChannelAvailabilityView; labelKey: string }) {
  const { t } = useTranslation('msg');
  const status = t(view.available ? 'msg:channels.available' : 'msg:channels.unavailable');
  const sample = view.isSample ? ` (${t('msg:channels.sample')})` : '';
  const own = view.tenantOwned ? ` — ${t('msg:channels.own_domain')}` : '';
  return (
    <Typography
      variant="body2"
      data-testid={`messaging-channel-${view.channel}`}
      data-available={view.available ? 'true' : 'false'}
    >
      {`${t(labelKey)}: ${status}${sample}${own}`}
    </Typography>
  );
}

export function ChannelsPage() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('msg.channel.manage');
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const settings = useQuery({
    queryKey: msgKeys.settings(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/settings')),
  });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: settings.data ? toForm(settings.data) : undefined,
  });
  const { reset } = form;

  useEffect(() => {
    if (settings.data) {
      reset(toForm(settings.data));
    }
  }, [settings.data, reset]);

  const save = useMutation({
    mutationFn: (values: UpdateMessagingSettingsRequest) =>
      unwrap(tenantApi.PUT('/api/v1/messaging/settings', { body: values })),
    onSuccess: (view) => {
      setSaved(true);
      queryClient.setQueryData(msgKeys.settings(session?.tenantId), view);
    },
  });
  const test = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/messaging/settings:send-test')),
  });

  const data = settings.data;
  return (
    <FormPage
      screenId={SCR_STAFF_950}
      titleKey="msg:channels.title"
      subtitleKey="msg:channels.subtitle"
      badges={
        data ? (
          <span data-testid="messaging-live" data-live={data.live ? 'true' : 'false'}>
            <StatusChip
              tone={data.live ? 'active' : 'pending'}
              labelKey={data.live ? 'msg:channels.live' : 'msg:channels.not_live'}
            />
          </span>
        ) : undefined
      }
      isLoading={settings.isPending}
      loadError={settings.error}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={save.error}
      readOnly={!write.canWrite}
      onSubmit={form.handleSubmit((values) => {
        setSaved(false);
        save.mutate(schema.parse(values));
      })}
      notice={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          {saved ? (
            <Alert severity="success" data-testid="messaging-settings-saved" onClose={() => setSaved(false)}>
              {t('msg:channels.saved')}
            </Alert>
          ) : null}
          {data && !data.live ? <Alert severity="info">{t('msg:channels.not_live_hint')}</Alert> : null}
        </Stack>
      }
    >
      {data ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <KeyValueList
            columns={3}
            items={[
              { labelKey: 'msg:channels.short_name', value: data.shortName },
              {
                labelKey: 'msg:channels.credit',
                value: <span data-testid="messaging-credit">{faDigits(String(data.creditBalance))}</span>,
              },
              {
                labelKey: 'msg:channels.lines',
                value: data.lines
                  .map(
                    (line) =>
                      `${t(`msg:channels.line.${line.kind}`)} (${t(`msg:channels.mode.${line.mode}`)})` +
                      (line.enabled ? '' : ` (${t('msg:channels.disabled')})`),
                  )
                  .join(' · '),
              },
              {
                labelKey: 'msg:channels.dedicated_available',
                value: (
                  <span
                    data-testid="messaging-dedicated-available"
                    data-available={data.dedicatedLineAvailable ? 'true' : 'false'}
                  >
                    {t(
                      data.dedicatedLineAvailable
                        ? 'msg:channels.dedicated_ready'
                        : 'msg:channels.dedicated_missing',
                    )}
                  </span>
                ),
              },
            ]}
          />
          {!data.marketingAllowed ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('msg:channels.marketing_paused')}
            </Alert>
          ) : null}
        </Paper>
      ) : null}
      <Typography variant="h3">{t('msg:channels.lines_section')}</Typography>
      <SelectRHF
        control={form.control}
        name="serviceLine"
        labelKey="msg:channels.service_line"
        helperKey="msg:channels.service_line_hint"
        options={[
          { value: 'Shared', labelKey: 'msg:channels.mode.Shared' },
          { value: 'Dedicated', labelKey: 'msg:channels.mode.Dedicated' },
        ]}
        disabled={!write.canWrite}
      />
      {data?.dedicatedLineAvailable ? (
        <Typography variant="body2" color="text.secondary">
          {t('msg:channels.dedicated_hint')}
        </Typography>
      ) : null}
      <SelectRHF
        control={form.control}
        name="signature"
        labelKey="msg:channels.signature"
        options={[
          { value: 'Prefix', labelKey: 'msg:channels.signature_position.Prefix' },
          { value: 'Suffix', labelKey: 'msg:channels.signature_position.Suffix' },
        ]}
        disabled={!write.canWrite}
      />
      <CheckboxRHF
        control={form.control}
        name="advertisingEnabled"
        labelKey="msg:channels.advertising"
        disabled={!write.canWrite}
      />
      {data ? (
        <>
          <Typography variant="h3">{t('msg:channels.channels_section')}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <ChannelStatus view={data.email} labelKey="msg:channels.email" />
            <ChannelStatus view={data.push} labelKey="msg:channels.push" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('msg:channels.email_hint')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('msg:channels.push_hint')}
          </Typography>
        </>
      ) : null}
      <Typography variant="h3">{t('msg:channels.hours_section')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={form.control}
          name="transactionalFrom"
          labelKey="msg:channels.transactional_from"
          disabled={!write.canWrite}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'transactional-from' } }}
        />
        <TextFieldRHF
          control={form.control}
          name="transactionalTo"
          labelKey="msg:channels.transactional_to"
          disabled={!write.canWrite}
          slotProps={{ htmlInput: { dir: 'ltr' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={form.control}
          name="marketingFrom"
          labelKey="msg:channels.marketing_from"
          disabled={!write.canWrite}
          slotProps={{ htmlInput: { dir: 'ltr' } }}
        />
        <TextFieldRHF
          control={form.control}
          name="marketingTo"
          labelKey="msg:channels.marketing_to"
          disabled={!write.canWrite}
          slotProps={{ htmlInput: { dir: 'ltr' } }}
        />
        <TextFieldRHF
          control={form.control}
          name="marketingCap"
          type="number"
          labelKey="msg:channels.marketing_cap"
          disabled={!write.canWrite}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          onClick={() => test.mutate()}
          disabled={!write.canWrite || test.isPending}
          data-testid="messaging-send-test"
        >
          {t('msg:channels.send_test')}
        </Button>
        {test.data ? (
          <Typography data-testid="messaging-send-test-result" data-status={test.data.status}>
            {t('msg:channels.send_test_result', { status: t(`msg:status.${test.data.status}`) })}
          </Typography>
        ) : null}
      </Stack>
      {test.error ? <ErrorState error={test.error} compact /> : null}
    </FormPage>
  );
}
